import {Button, Container, Nav, Navbar, NavDropdown} from "react-bootstrap"
import React, {FC, useEffect, useMemo, useState} from "react"
import {Link, useLocation} from "react-router-dom"
import {useTranslation} from "react-i18next"
import {useTheme} from "../../context/ThemeProvider"
import {useAuth} from "../../context/AuthProvider"
import {getNavigationItems, NavigationItem} from "./navigation.config"
import styles from "./MainNavigation.module.scss"
import axios from "axios"

type Props = {
    showSidebar: boolean
    sidebarCollapsed: boolean
    toggleSidebar: () => void
    openSidebar: () => void
}

const MainNavbar: FC<Props> = ({
    showSidebar,
    sidebarCollapsed,
    toggleSidebar,
    openSidebar,
}) => {
    const {t, i18n} = useTranslation(["common", "tenant", "navigation", "booking", "info", "site", "settings"])
    const theme = useTheme()
    const auth = useAuth()
    const {axiosAuthHeader} = auth
    const location = useLocation()

    const loggedIn = auth.isAuthenticated()
    const useSidebarForMobile =
        showSidebar && loggedIn
    const [temperature, setTemperature] = useState<any>(null)

    const visibleNavigationItems = useMemo(() => {
        return getNavigationItems(auth, t).filter(
            (item: NavigationItem) => item.visible
        )
    }, [auth, t])

    const topNavigationItems = visibleNavigationItems.filter(
        (item: NavigationItem) => item.section === "workspace"
    )

    const administrationItems = visibleNavigationItems.filter(
        (item: NavigationItem) => item.section === "administration"
    )

    const active = (path: string): boolean => {
        return location.pathname === path ||
            location.pathname.startsWith(path + "/")
    }

    const changeLanguageWrapper = (lang: string) => {
        i18n.changeLanguage(lang).then(() => {
            if (loggedIn) {
                axios.get("/api/user/profile/lang/" + lang, auth.axiosAuthHeader()).then()
            }
        })
    }

    const handleSidebarButton = () => {
        if (window.innerWidth < 992) {
            openSidebar()
            return
        }

        toggleSidebar()
    }

    useEffect(() => {
        if (!loggedIn) {
            setTemperature(null)
            return
        }

        axios.get("/api/climate/get/latest/notfunctionalyet", axiosAuthHeader()).then(result => {
            setTemperature(result.data)
        }).catch(() => {
            setTemperature(null)
        })
    }, [auth, axiosAuthHeader, loggedIn])

    const renderNavigationItem = (item: NavigationItem) => (
        <Nav.Link
            key={item.id}
            as={Link}
            eventKey={item.to}
            to={item.to}
        >
            <i className={`bi ${item.icon}`}></i>{" "}
            {item.label}
        </Nav.Link>
    )

    return (
        <Navbar
            fixed="top"
            expand="lg"
            className={`bg-body-tertiary ${styles.navbar}`}
        >
            <Container fluid className={styles.navbarContainer}>
                {showSidebar && loggedIn ? (
                    <Button
                        variant="outline-secondary"
                        className="me-2"
                        onClick={handleSidebarButton}
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <i className="bi bi-list"></i>
                    </Button>
                ) : null}

                <Navbar.Brand as={Link} to="/" className={styles.navbarBrand}>
                    <img
                        src={theme.getTheme() === "dark" ? "/favicon.svg" : "/favicon-light.svg"}
                        width="30"
                        height="30"
                        className="d-inline-block align-top"
                        alt="Exam console logo"
                    />{" "}
                    {t("title", {ns: "navigation"})}
                </Navbar.Brand>

                {!loggedIn ? (
                    <Nav.Link
                        href="/"
                        className={`text-success ${styles.loginHint}`}
                        onClick={event => {
                            event.preventDefault()
                            auth.login("/booking")
                        }}
                    >
                        <i className="bi-box-arrow-in-right"></i>{" "}
                        {t("examPlatformHint", {ns: "navigation"})}
                    </Nav.Link>
                ) : null}

                {!useSidebarForMobile ? (
                    <Navbar.Toggle
                        aria-controls="basic-navbar-nav"
                    />
                ) : null}

                <Navbar.Collapse
                    id="basic-navbar-nav"
                    className={
                        useSidebarForMobile
                            ? `d-none d-lg-flex ${styles.navbarCollapse}`
                            : styles.navbarCollapse
                    }
                >
                    <Nav className="me-auto" activeKey={location.pathname}>
                        {loggedIn ? (
                            <Nav.Link as={Link} eventKey="/booking" to="/booking">
                                <i className="bi-ui-checks"></i>{" "}
                                {t("book.book", {ns: "booking"})}
                            </Nav.Link>
                        ) : null}

                        <NavDropdown title={<><i className="bi bi-info-circle"></i> {t("start", {ns: "navigation"})}</>}>
                            <NavDropdown.Item as={Link} eventKey="/" to="/">
                                <i className="bi bi-exclamation-circle"></i>{" "}
                                {t("info.title", {ns: "info"})}
                            </NavDropdown.Item>

                            <NavDropdown.Item as={Link} eventKey="/faq" to="/faq">
                                <i className="bi bi-question-circle"></i>{" "}
                                {t("faq.title", {ns: "info"})}
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                as={Link}
                                eventKey="/climate/6784d8aeb2e2dd156850e00a"
                                to="/climate/6784d8aeb2e2dd156850e00a"
                            >
                                <i className="bi bi-thermometer-half"></i>{" "}
                                {t("site.location", {ns: "site"})}
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                target="_blank"
                                href="https://asset.e-learning.tu-darmstadt.de/d/pruefungondemand_360/"
                            >
                                <i className="bi bi-compass"></i>{" "}
                                {t("roomTour", {ns: "navigation"})}
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </NavDropdown.Item>
                        </NavDropdown>

                        {!showSidebar && topNavigationItems.map(
                            (item: NavigationItem) => renderNavigationItem(item)
                        )}

                        {!showSidebar && administrationItems.length > 0 ? (
                            <NavDropdown title={<><i className="bi bi-gear-wide-connected"></i> {t("administration", {ns: "navigation"})}</>}>
                                {administrationItems.map((item: NavigationItem) => (
                                    <NavDropdown.Item
                                        key={item.id}
                                        as={Link}
                                        eventKey={item.to}
                                        to={item.to}
                                        active={active(item.to)}
                                    >
                                        <i className={`bi ${item.icon}`}></i>{" "}
                                        {item.label}
                                    </NavDropdown.Item>
                                ))}
                            </NavDropdown>
                        ) : null}

                    </Nav>

                    <hr className="d-lg-none my-2"/>

                    <Nav>
                        {loggedIn && auth.sessionRecovery ? (
                            <Nav.Link className="text-danger" href="#" onClick={() => auth.recoverSession()}>
                                <i className="bi bi-box-arrow-right"></i>{" "}
                                {t("recoverSession", {ns: "navigation"})}
                            </Nav.Link>
                        ) : null}

                        {temperature ? (
                            <Nav.Link
                                as={Link}
                                eventKey="/climate/6784d8aeb2e2dd156850e00a"
                                to="/climate/6784d8aeb2e2dd156850e00a"
                            >
                                <i className="bi bi-thermometer-half"></i>{" "}
                                L4|02 335: <b>{temperature.temperature}°C</b>
                            </Nav.Link>
                        ) : null}

                        {auth.isActualAdmin() ? (
                            <NavDropdown
                                title={<>
                                    <i className="bi bi-person-bounding-box"></i>{" "}
                                    {auth.rolePreview
                                        ? t(`rolePreview.roles.${auth.rolePreview}`, {
                                            ns: "navigation",
                                        })
                                        : t("rolePreview.title", {ns: "navigation"})}
                                </>}
                                id="role-preview-nav-dropdown"
                            >
                                <NavDropdown.Header>
                                    {t("rolePreview.description", {ns: "navigation"})}
                                </NavDropdown.Header>
                                <NavDropdown.Item
                                    active={auth.rolePreview === null}
                                    onClick={() => auth.setRolePreview(null)}
                                >
                                    {t("rolePreview.actualAdmin", {ns: "navigation"})}
                                </NavDropdown.Item>
                                <NavDropdown.Divider/>
                                {([
                                    "examOfficeEmployee",
                                    "examOfficeAssistant",
                                    "dean",
                                    "infrastructure",
                                    "facultyOwner",
                                    "deputy",
                                    "disabilityOfficer",
                                    "reader",
                                ] as const).map(role => (
                                    <NavDropdown.Item
                                        key={role}
                                        active={auth.rolePreview === role}
                                        onClick={() => auth.setRolePreview(role)}
                                    >
                                        {t(`rolePreview.roles.${role}`, {ns: "navigation"})}
                                    </NavDropdown.Item>
                                ))}
                            </NavDropdown>
                        ) : null}

                        <NavDropdown
                            title={<><i className="bi-translate"></i> {t("settings", {ns: "navigation"})}</>}
                            id="settings-nav-dropdown"
                        >
                            <NavDropdown.Item
                                disabled={i18n.language === "de"}
                                onClick={() => changeLanguageWrapper("de")}
                            >
                                <i className="bi-translate"></i>{" "}
                                {t("language.german", {ns: "navigation"})}
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                disabled={i18n.language === "en"}
                                onClick={() => changeLanguageWrapper("en")}
                            >
                                <i className="bi-translate"></i>{" "}
                                {t("language.english", {ns: "navigation"})}
                            </NavDropdown.Item>

                            <NavDropdown.Divider/>

                            <NavDropdown.Item
                                disabled={theme.getTheme() === "dark"}
                                data-bs-theme-value="dark"
                                aria-pressed={true}
                                onClick={() => theme.setTheme("dark")}
                            >
                                <i className="bi-moon-stars-fill"></i>{" "}
                                {t("theme.dark", {ns: "navigation"})}
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                disabled={theme.getTheme() === "light"}
                                data-bs-theme-value="light"
                                onClick={() => theme.setTheme("light")}
                            >
                                <i className="bi-brightness-high-fill"></i>{" "}
                                {t("theme.light", {ns: "navigation"})}
                            </NavDropdown.Item>

                            {loggedIn ? (
                                <>
                                    <NavDropdown.Divider/>
                                    <NavDropdown.Item as={Link} eventKey="/settings" to="/settings">
                                        <i className="bi bi-person-gear"></i>{" "}
                                        {t("user.settings.title", {ns: "settings"})}
                                    </NavDropdown.Item>
                                </>
                            ) : null}
                        </NavDropdown>

                        <NavDropdown
                            title={<><i className="bi bi-person-lines-fill"></i> {t("contact.title", {ns: "navigation"})}</>}
                            id="contact-nav-dropdown"
                        >
                            <NavDropdown.Item
                                href={
                                    "mailto:e-pruefungen@tu-darmstadt.de?subject=" +
                                    t("mailto.subject", {ns: "navigation"}) +
                                    "&body=" +
                                    t("mailto.body", {ns: "navigation"})
                                }
                                target="_blank"
                            >
                                <i className="bi bi-envelope"></i>{" "}
                                {t("contact.mailIssue", {ns: "navigation"})}
                            </NavDropdown.Item>

                            <NavDropdown.Divider/>

                            <NavDropdown.Item
                                href="https://www.hda.tu-darmstadt.de/start_hda/arbeitsbereiche_hda/hda_team_details_117696.de.jsp"
                                target="_blank"
                            >
                                <i className="bi bi-person-fill-gear"></i>{" "}
                                {t("responsible", {ns: "navigation"})}
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                className="text-wrap"
                                href="https://www.e-learning.tu-darmstadt.de/"
                                target="_blank"
                            >
                                <i className="bi bi-people-fill"></i>{" "}
                                {t("contact.unit", {ns: "navigation"})}
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </NavDropdown.Item>

                            <NavDropdown.Divider/>

                            <NavDropdown.Item
                                href="https://www.e-learning.tu-darmstadt.de/impressum.de.jsp"
                                target="_blank"
                            >
                                <i className="bi bi-info-square"></i>{" "}
                                {t("impress", {ns: "navigation"})}
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </NavDropdown.Item>

                            <NavDropdown.Item
                                href="https://www.tu-darmstadt.de/datenschutzerklaerung.de.jsp"
                                target="_blank"
                            >
                                <i className="bi bi-info-square"></i>{" "}
                                {t("privacyPolicy", {ns: "navigation"})}
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </NavDropdown.Item>
                        </NavDropdown>

                        {loggedIn ? (
                            <NavDropdown
                                title={<><i className="bi bi-box-arrow-right"></i> {t("logout.title", {ns: "navigation"})}</>}
                            >
                                <NavDropdown.Item onClick={() => auth.logOutLocally()}>
                                    {t("logout.local", {ns: "navigation"})}
                                </NavDropdown.Item>

                                <NavDropdown.Item onClick={() => auth.logOutGlobally()}>
                                    {t("logout.global", {ns: "navigation"})}
                                </NavDropdown.Item>
                            </NavDropdown>
                        ) : (
                            <Nav.Link onClick={() => auth.login()}>
                                <i className="bi bi-box-arrow-in-right"></i>{" "}
                                {t("login", {ns: "navigation"})}
                            </Nav.Link>
                        )}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    )
}

export default MainNavbar
